import React, { useState, useEffect } from "react";
import { Container, Row, Col, Spinner, Alert, Button, Badge } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import useApi from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import CourseRecommendationModal from "../component/CourseRecommendationModal";
import InterestModal from "../component/InterestModal";

const normalizeAgeGroup = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "";
  if (raw === "18_24" || raw === "18-24" || raw === "18-24 years") return "18_24";
  if (raw === "25_34" || raw === "25-34" || raw === "25-34 years") return "25_34";
  if (raw === "35_plus" || raw === "35+" || raw === "35+ years") return "35_plus";
  return raw;
};

const getRecommendationScore = (course, answers) => {
  const content = `${course.title || ""} ${course.description || ""}`.toLowerCase();
  const hasAny = (keywords) => keywords.some((keyword) => content.includes(keyword));
  const duration = Number.parseInt(course.duration, 10) || 999;
  const ageGroups = Array.isArray(course.recommendation_age_groups)
    ? course.recommendation_age_groups.map(normalizeAgeGroup).filter(Boolean)
    : [];

  let score = 0;
  if (ageGroups.length > 0) {
    score += ageGroups.includes(normalizeAgeGroup(answers.ageGroup)) ? 8 : -4;
  }
  if (answers.carType === "manual" && hasAny(["manual", "gear", "clutch"])) score += 3;
  if (answers.carType === "automatic" && hasAny(["automatic", "auto"])) score += 3;
  if (answers.preferredTime === "morning" && hasAny(["morning", "am", "early"])) score += 2;
  if (answers.preferredTime === "afternoon" && hasAny(["afternoon", "day"])) score += 2;
  if (answers.preferredTime === "evening" && hasAny(["evening", "pm"])) score += 2;
  if (answers.preferredTime === "weekend" && hasAny(["weekend", "saturday", "sunday"])) score += 2;
  if (answers.driverPreference === "female" && hasAny(["female instructor", "lady instructor", "women instructor"])) score += 3;
  if (answers.driverPreference === "male" && hasAny(["male instructor"])) score += 2;
  if (answers.ageGroup === "35_plus" && duration <= 18) score += 1;
  if (answers.ageGroup === "18_24" && duration >= 10 && duration <= 30) score += 1;

  return score;
};

const Course = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredCourse, setHoveredCourse] = useState(null);
  const [showRecommendationModal, setShowRecommendationModal] = useState(false);
  const [showInterestModal, setShowInterestModal] = useState(false);
  const [selectedInterestCourseId, setSelectedInterestCourseId] = useState(null);
  const [appliedPreferences, setAppliedPreferences] = useState(null);
  const { publicAuth } = useApi();
  const { currentCity } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const observerOptions = { threshold: 0.12, rootMargin: "0px 0px -10% 0px" };
    let observer;
    let timer;
    const controller = new AbortController();

    const animateOnScroll = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const el = entry.target;
          el.classList.add("is-visible");
          const animationType = el.dataset.animation;
          if (animationType) el.classList.add(animationType);
          const delay = el.dataset.delay || "0";
          el.style.transitionDelay = `${parseInt(delay, 10)}ms`;
          observer.unobserve(el);
        }
      });
    };

    observer = new IntersectionObserver(animateOnScroll, observerOptions);

    const fetchCourses = async () => {
      try {
        setLoading(true);

        let url = "courses";
        if (currentCity) url += `?city=${encodeURIComponent(currentCity)}`;

        const response = await publicAuth(url, "GET", undefined, {
          signal: controller.signal,
        });

        if (controller.signal.aborted) return;

        if (response?.data?.status) {
          setCourses(response.data.data || []);
          setError(null);
        } else {
          throw new Error(response?.data?.message || "Failed to fetch courses");
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          setError(err?.message || "Something went wrong");
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };

    fetchCourses();

    timer = window.setTimeout(() => {
      document.querySelectorAll(".animated-section").forEach((el) => observer.observe(el));
    }, 0);

    return () => {
      controller.abort();
      observer && observer.disconnect();
      window.clearTimeout(timer);
    };
  }, [currentCity]); // 🔥 City change par bhi data aayega, refresh par bhi

  const openRegisterForCourse = (courseId) => {
    navigate("/enroll", { state: { selectedCourseId: courseId } });
  };

  const openInterestForCourse = (courseId) => {
    setSelectedInterestCourseId(courseId);
    setShowInterestModal(true);
  };

  const openRecommendationModal = () => {
    setShowRecommendationModal(true);
  };

  const applyRecommendationPreferences = (answers) => {
    setAppliedPreferences(answers);
  };

  

  const hasPriceForCurrentCity = (course) => {
    return currentCity && course.price && course.price.total_price;
  };

  const getDisplayPrice = (course) => {
    if (hasPriceForCurrentCity(course)) {
      return `₹${String(course.price.total_price).split(".")[0]}`;
    }
    return null;
  };

  const recommendationRows = appliedPreferences
    ? [...courses]
        .map((course) => ({
          course,
          score: getRecommendationScore(course, appliedPreferences),
          duration: Number.parseInt(course.duration, 10) || 999,
        }))
        .sort((a, b) => {
          if (b.score !== a.score) return b.score - a.score;
          return a.duration - b.duration;
        })
    : [];

  const recommendationScoreMap = recommendationRows.reduce((acc, row) => {
    acc[row.course.id] = row.score;
    return acc;
  }, {});

  const recommendedCourseIds = new Set(
    recommendationRows
      .filter((row) => row.score > 0)
      .slice(0, 3)
      .map((row) => row.course.id)
  );

  const orderedCourses = appliedPreferences
    ? recommendationRows.map((row) => row.course)
    : courses;

  // ---------------- UI STATES ------------------

  if (loading) {
    return (
      <section className="py-5 my-5">
        <Container className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading courses...</p>
        </Container>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-5 my-5">
        <Container className="text-center py-5">
          <Alert variant="danger">Error: {error}</Alert>
        </Container>
      </section>
    );
  }

  if (courses.length === 0) {
    return (
      <section className="py-5 my-5">
        <Container className="text-center py-5">
          <Alert variant="info">
            {currentCity ? `No courses available in ${currentCity}` : "No courses available"}
          </Alert>
        </Container>
      </section>
    );
  }

  // ---------------- MAIN UI ------------------

  return (
    <section className="py-4 my-3 position-relative">
      <Container>
        <div
          className="text-center mb-3 animated-section"
          data-animation="fadeInUp"
        >
          <Badge
            className="px-3 py-2 mb-2"
            style={{
              background: "#eef3ff",
              color: "#1c3894",
              border: "1px solid #d8e2ff",
              borderRadius: "999px",
              fontSize: "0.78rem",
              fontWeight: 700,
              letterSpacing: "0.04em",
            }}
          >
            Smart Course Selection
          </Badge>
          <h2
            className="fw-bold mb-2"
            style={{ fontSize: "clamp(1.8rem, 3.8vw, 3.2rem)", lineHeight: 1.02 }}
          >
            Driving Courses That Fit Your Journey
          </h2>
          <p className="text-muted mb-0" style={{ fontSize: "clamp(0.98rem, 1.6vw, 1.25rem)" }}>
            {currentCity
              ? `Explore focused training in ${currentCity}, matched to your goals.`
              : "Explore focused training, matched to your goals."}
          </p>

          {!currentCity && (
            <Alert variant="warning" className="mt-3 d-inline-block">
              <i className="bi bi-info-circle me-2"></i>
              Set your location to see course prices
            </Alert>
          )}
        </div>

        {appliedPreferences ? (
          <Alert
            variant="primary"
            className="d-flex flex-wrap align-items-center justify-content-between gap-3 rounded-4 border-0 mb-3"
            style={{
              background: "linear-gradient(135deg, #eef3ff 0%, #e8efff 100%)",
              color: "#16307f",
              boxShadow: "0 12px 28px rgba(26, 56, 148, 0.08)",
            }}
          >
            <div>
              Recommended matches are now shown first.
            </div>
            <Button
              variant="outline-primary"
              className="rounded-pill px-4"
              onClick={() => setAppliedPreferences(null)}
            >
              Reset Filter
            </Button>
          </Alert>
        ) : null}

        <div
          className="animated-section mb-3"
          data-animation="fadeInUp"
          data-delay="80"
          style={{
            borderRadius: "22px",
            padding: "16px 20px",
            background: "linear-gradient(135deg, #f6f8ff 0%, #edf2ff 100%)",
            border: "1px solid #dbe4ff",
            boxShadow: "0 14px 28px rgba(24, 48, 120, 0.07)",
          }}
        >
         
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "18px",
            }}
          >
            <div>
              <div style={{ fontSize: "1rem", fontWeight: 800, color: "#132b8f", marginBottom: "2px" }}>
                Need a quick recommendation?
              </div>
              <div style={{ fontSize: "0.9rem", color: "#4b5d87" }}>
                Answer 5 questions and we will surface your best-fit courses.
              </div>
            </div>
            <button
              type="button"
              onClick={openRecommendationModal}
              style={{
                border: "0",
                borderRadius: "10px",
                background: "#1f2799",
                color: "#ffffff",
                padding: "10px 18px",
                fontSize: "0.84rem",
                fontWeight: 700,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                boxShadow: "0 16px 28px rgba(31, 39, 153, 0.25)",
              }}
            >
              Get Recommended Courses
            </button>
          </div>
        </div>

        {orderedCourses.map((course, index) => {
          const recommendationMeta = {
            isRecommended: Boolean(appliedPreferences) && recommendedCourseIds.has(course.id),
            score: recommendationScoreMap[course.id] || 0,
          };

          return (
          <div
            key={course.id}
            className={`mb-4 pb-4 ${index < orderedCourses.length - 1 ? "border-bottom" : ""}`}
            style={{
              borderRadius: "28px",
              padding: "22px",
              background: "linear-gradient(180deg, #ffffff 0%, #fbfcff 100%)",
              boxShadow: "0 18px 38px rgba(21, 38, 77, 0.06)",
              border: "1px solid #edf1fb",
            }}
          >
            <Row className={`align-items-center g-5 `}>
              
              {/* IMAGE */}
              

              {/* CONTENT */}
              <Col
                lg={6}
                className="animated-section"
                data-animation="fadeInLeft"
                data-delay={index * 100 + 100}
              >
                <div className={`ps-lg-5 ${index % 2 === 0 ? "" : "pe-lg-5"}`}>
                  
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex flex-wrap gap-2">
                      <Badge bg="primary" className="fs-6 px-3 py-2">Learner Course</Badge>
                      {recommendationMeta.isRecommended ? (
                        <Badge
                          className="fs-6 px-3 py-2 d-inline-flex align-items-center"
                          style={{
                            background: "linear-gradient(135deg, #fff7cc 0%, #ffd24d 100%)",
                            color: "#6b4b00",
                            border: "1px solid #f1c232",
                            boxShadow: "0 10px 18px rgba(241, 194, 50, 0.22)",
                            gap: "8px",
                          }}
                        >
                          <i className="bi bi-award-fill" aria-hidden="true"></i>
                          Recommended
                        </Badge>
                      ) : null}
                    </div>

                    {hasPriceForCurrentCity(course) && (
                      <div className="price-tag bg-danger text-white p-2 px-3 rounded-pill shadow-sm">
                        <span className="d-block fs-6 fw-normal">Starting from</span>
                        <span className="d-block fs-4 fw-bold">{getDisplayPrice(course)}</span>
                      </div>
                    )}
                  </div>

                  <h2 className="fw-bold mb-3 text-danger">{course.title}</h2>
                  <p>{course.description}</p>

                  <div className="course-details-card bg-light p-2 rounded-4 mb-1">
                    <Row className="g-3">
                      <Col sm={6}>
                        <div className="d-flex align-items-center">
                          <div className="bg-white p-3 rounded-3 me-3 shadow-sm">
                            <img src={`${process.env.PUBLIC_URL}/assets/images/icons/icon-6.png`} alt="Theory" width="32" />
                          </div>
                          <div>
                            <h6 className="mb-1 fw-bold text-danger">Theory Session</h6>
                            <p className="mb-0 text-dark">{course.duration} Day</p>
                          </div>
                        </div>
                      </Col>
                      <Col sm={6}>
                        <div className="d-flex align-items-center">
                          <div className="bg-white p-3 rounded-3 me-3 shadow-sm">
                            <img src={`${process.env.PUBLIC_URL}/assets/images/icons/icon-7.png`} alt="Practical" width="32" />
                          </div>
                          <div>
                            <h6 className="mb-1 fw-bold text-danger">Practical Session</h6>
                            <p className="mb-0 text-dark">{course.practical} Hours</p>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>

                  <div className="alert alert-info border-0 rounded-4 mb-3">
                    <i className="bi bi-info-circle-fill me-2"></i>
                    Driving route will be charged separately.
                    {currentCity && <span className="ms-1">Prices for {currentCity}</span>}
                  </div>

                  <div className="d-flex gap-2">
                    <Button
                      variant="outline-secondary"
                      className="rounded-pill px-4"
                      onClick={() => navigate(`/course/${course.slug}`)}
                    >
                      Explore Now
                    </Button>

                    <Button
                      variant="primary"
                      className="rounded-pill px-4"
                      onClick={() => openInterestForCourse(course.id)}
                    >
                      Register Interest
                    </Button>
                  </div>
                </div>
              </Col>
              <Col
                lg={6}
                className="animated-section"
                data-animation="fadeInRight"
                data-delay={index * 100}
              >
                <div
                  className="position-relative rounded-4 overflow-hidden shadow-lg course-image-container"
                  onMouseEnter={() => setHoveredCourse(course.id)}
                  onMouseLeave={() => setHoveredCourse(null)}
                >
                  <img
                    src={`${process.env.REACT_APP_IMAGE_URL.replace("/API/index.php", "/API")}/${course.image}`}
                    alt={course.title}
                    className={`img-fluid w-100 course-image ${hoveredCourse === course.id ? "hovered" : ""}`}
                    style={{ height: "400px", objectFit: "contain", transition: "all 0.3s ease" }}
                    onError={(e) => {
                      e.currentTarget.src = `${process.env.PUBLIC_URL}/assets/images/course${
                        index % 2 === 0 ? "1" : "2"
                      }.png`;
                    }}
                  />
                </div>
              </Col>

            </Row>
          </div>
        );
        })} 
      </Container>
      <CourseRecommendationModal
        show={showRecommendationModal}
        onClose={() => setShowRecommendationModal(false)}
        courses={courses}
        onSelectCourse={openRegisterForCourse}
        onViewCourse={(course) => navigate(`/course/${course.slug}`)}
        onApplyPreferences={applyRecommendationPreferences}
      />
      <InterestModal
        show={showInterestModal}
        onClose={() => {
          setShowInterestModal(false);
          setSelectedInterestCourseId(null);
        }}
        courseId={selectedInterestCourseId}
      />
    </section>
  );
};

export default Course; 
